import React, { useState } from 'react';
import { Sparkles, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const AISearchChat = ({ 
  onSearch, 
  isLoading = false, 
  placeholder = "Inserisci una città (es: Roma, Milano, Firenze...)",
  compact = false
}) => {
  const [query, setQuery] = useState('');

  const handleSearch = () => {
    if (query.trim() && onSearch) {
      onSearch(query.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (compact) {
    return (
      <div className="flex gap-3 w-full max-w-2xl mx-auto">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="pl-12 h-12 text-base border-2 border-slate-200 rounded-xl focus:border-violet-500 focus:ring-violet-500"
            disabled={isLoading}
          />
        </div>
        <Button
          onClick={handleSearch}
          disabled={isLoading || !query.trim()}
          className="h-12 px-6 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-lg shadow-violet-500/25"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Sparkles className="w-5 h-5 mr-2" />
              Cerca
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/50 border-0 shadow-xl shadow-violet-500/10 p-8 rounded-3xl">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,transparent,black)] opacity-40" />
      
      {/* Header */}
      <div className="relative flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Trova Offerte con AI</h2>
            <p className="text-slate-600">Lascia che l'intelligenza artificiale trovi le migliori offerte per te</p>
          </div>
        </div>
        <Badge className="bg-violet-100 text-violet-700 border-violet-200 px-3 py-1.5 text-sm font-semibold">
          GPT-5.2
        </Badge>
      </div>

      {/* Search Input */}
      <div className="relative flex gap-4">
        <div className="flex-1 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-2xl blur opacity-20" />
          <div className="relative bg-white rounded-2xl border-2 border-slate-200 hover:border-violet-300 transition-colors overflow-hidden shadow-sm">
            <div className="flex items-center px-5 py-4">
              <Search className="w-6 h-6 text-slate-400 mr-4 flex-shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                className="flex-1 text-lg text-slate-800 placeholder-slate-400 outline-none bg-transparent"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <Button
          onClick={handleSearch}
          disabled={isLoading || !query.trim()}
          className="h-auto px-8 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-2xl font-semibold text-lg shadow-xl shadow-violet-500/30 transition-all hover:shadow-2xl hover:shadow-violet-500/40 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {isLoading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <Sparkles className="w-6 h-6 mr-2" />
              Cerca con AI
            </>
          )}
        </Button>
      </div>

      {/* Quick suggestions */}
      <div className="relative mt-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-slate-500">Popolari:</span>
        {['Roma', 'Milano', 'Firenze', 'Venezia', 'Napoli'].map((city) => (
          <button
            key={city}
            onClick={() => {
              setQuery(city);
              if (onSearch) onSearch(city);
            }}
            className="px-3 py-1.5 text-sm bg-white hover:bg-violet-50 text-slate-600 hover:text-violet-700 rounded-lg border border-slate-200 hover:border-violet-300 transition-colors"
            disabled={isLoading}
          >
            {city}
          </button>
        ))}
      </div>
    </Card>
  );
};

export default AISearchChat;
